import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  TablePagination,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  alpha,
} from '@mui/material'

import { ArrowForwardIos } from '@mui/icons-material'
import { PaginationResponse } from '@/state/root.models'
import { colors } from '@/theme/colors.theme'

export interface Props {
  pagination: PaginationResponse
  tableDense?: boolean
  small?: boolean
  leftContent?: React.ReactNode
  rowsPerPageOptions?: number[]
  handleChangePage: (newPage: number) => void
  handleChangeLimit?: (numRows: number) => void
  handleSetTableDense?: () => void
}

const PaginationTable = ({
  pagination,
  tableDense,
  small,
  leftContent,
  rowsPerPageOptions,
  handleChangePage,
  handleChangeLimit,
  handleSetTableDense,
}: Props) => {
  const { t } = useTranslation()

  const [pageNumber, setPageNumber] = React.useState('')

  const disabledChangePage =
    (pagination.total !== -1 && Number(pageNumber) > pagination.pages) ||
    pageNumber === '' ||
    Number(pageNumber) < 1 ||
    Number(pageNumber) === pagination.page

  const handleOnChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    handleChangePage(newPage + 1)
  }

  const handleOnChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    handleChangeLimit(Number(event.target.value))
  }

  const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(event.target.value)
  }

  const handleGoToPage = () => {
    handleChangePage(Number(pageNumber))
    setPageNumber('')
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' && !disabledChangePage) {
      handleGoToPage()
    }
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      p={2}
    >
      <Box mt={2} display="flex" gap={leftContent ? 4 : 0}>
        <Box>{leftContent && <>{leftContent}</>}</Box>
        {handleSetTableDense && (
          <Box
            borderLeft={
              leftContent ? `1px solid ${alpha(colors.white, 0.2)}` : 'none'
            }
            paddingLeft={leftContent ? 4 : 0}
          >
            <FormControlLabel
              control={
                <Switch value={tableDense} onChange={handleSetTableDense} />
              }
              label="Dense"
            />
          </Box>
        )}
      </Box>
      <Box display="flex" alignItems="center" pt={2}>
        <Box ml="auto">
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page - 1}
            onPageChange={handleOnChangePage}
            rowsPerPage={pagination.limit}
            onRowsPerPageChange={handleOnChangeRowsPerPage}
            rowsPerPageOptions={rowsPerPageOptions}
          />
        </Box>
        {!small && (
          <>
            <Box ml={3}>
              <Typography variant="body2">{t('labels.goToPage')}</Typography>
            </Box>
            <Box ml={1} width={50}>
              <TextField
                size="small"
                onKeyDown={onKeyDown}
                fullWidth
                type="number"
                name="search"
                margin="none"
                variant="outlined"
                value={pageNumber}
                onChange={handleChangeInput}
                inputProps={{
                  min: '1',
                  step: '1',
                  autoComplete: 'off',
                  pattern: 'd*',
                }}
              />
            </Box>
            <Button
              size="small"
              variant="text"
              className="white"
              disabled={disabledChangePage}
              onClick={handleGoToPage}
              endIcon={<ArrowForwardIos style={{ fontSize: 12 }} />}
              sx={{
                minWidth: 64,
                marginLeft: 1,
              }}
            >
              {t('labels.go')}
            </Button>
          </>
        )}
      </Box>
    </Box>
  )
}

export default PaginationTable
